import { INSPECTION_PAGINATION_STORE_NAME } from '@/constants/constants';
import { INSPECTION_LIST_DEFAULT_PAGE_SIZE } from '@/app/_libs/constants/constants';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Pagination state for the two paginated inspection lists:
 *
 * - `current`  — /work-progress, SIMPLE and SPLIT presentations
 * - `analyzed` — /work-orders/analyzed/:id, SIMPLE and SPLIT presentations
 *
 * The two views are FULLY INDEPENDENT — page size and sort included. They page
 * different lists for different purposes (a live screen versus a finished order),
 * and one shared page size meant changing it on one screen silently repaged the
 * other. One store rather than two only so the persistence and hydration plumbing
 * is written once.
 *
 * Deliberately a store rather than hook state: the pagination bar lives deep in the
 * view containers while the fetching lives in the hooks, and prop-drilling the pair
 * would couple the 2D branches to pagination they do not have.
 *
 * Writing to this store IS how a page change interrupts an in-flight request — both
 * hooks key their fetch effect on these values.
 *
 * Only resourcesPerPage and sort are persisted, and everything is wiped on operator
 * switch via clearClientStores; these are shared multi-operator workstations.
 */
export type InspectionListView = 'current' | 'analyzed';

type ViewPagination = {
  /** Server-reported row count across all pages, not the current page's length. */
  totalInspections: number;
  totalPages: number;
  currentPage: number;
  resourcesPerPage: number;
  sort: string;
};

interface InspectionPaginationState {
  views: Record<InspectionListView, ViewPagination>;
  // Persisted state rehydrates asynchronously. The hooks wait for this before their
  // first fetch, otherwise a reload requests the default page size and then
  // immediately requests the persisted one.
  isStoreHydrated: boolean;
}

type Actions = {
  /** Operator-driven page change. Triggers a refetch of that view. */
  setCurrentPage: (view: InspectionListView, page: number) => void;
  /**
   * Server-reported totals and echoed page. Separate from setCurrentPage so it is
   * obvious at the call site which writes come from the operator and which from a
   * response.
   */
  setPageMeta: (
    view: InspectionListView,
    meta: { totalInspections: number; totalPages: number; currentPage: number }
  ) => void;
  /** Also returns that view to page 1: page N may not exist at the new size. */
  setResourcesPerPage: (view: InspectionListView, size: number) => void;
  /**
   * Back to page 1 with empty totals — no work order to page through. Deliberately
   * preserves resourcesPerPage and sort: /work-progress calls this on every 404
   * poll tick, and resetting the page size there would wipe the operator's choice
   * every five seconds.
   */
  resetView: (view: InspectionListView) => void;
  setIsStoreHydrated: (isHydrated: boolean) => void;
  resetStore: () => void;
};

const INITIAL_VIEW: ViewPagination = {
  totalInspections: 0,
  totalPages: 0,
  currentPage: 1,
  resourcesPerPage: INSPECTION_LIST_DEFAULT_PAGE_SIZE,
  sort: 'desc'
};

const INITIAL_VIEWS: Record<InspectionListView, ViewPagination> = {
  current: INITIAL_VIEW,
  analyzed: INITIAL_VIEW
};

export const useInspectionPaginationStore = create<
  InspectionPaginationState & Actions
>()(
  persist(
    (set, get) => ({
      views: INITIAL_VIEWS,
      isStoreHydrated: false,
      setCurrentPage: (view, page) => {
        set({
          views: {
            ...get().views,
            [view]: { ...get().views[view], currentPage: page }
          }
        });
      },
      setPageMeta: (view, meta) => {
        set({
          views: { ...get().views, [view]: { ...get().views[view], ...meta } }
        });
      },
      setResourcesPerPage: (view, size) => {
        set({
          views: {
            ...get().views,
            [view]: {
              ...get().views[view],
              resourcesPerPage: size,
              currentPage: 1
            }
          }
        });
      },
      resetView: (view) => {
        set({
          views: {
            ...get().views,
            [view]: {
              ...get().views[view],
              totalInspections: 0,
              totalPages: 0,
              currentPage: 1
            }
          }
        });
      },
      setIsStoreHydrated: (isHydrated: boolean) => {
        set({ isStoreHydrated: isHydrated });
      },
      resetStore: () => {
        set({ views: INITIAL_VIEWS });
      }
    }),
    {
      name: INSPECTION_PAGINATION_STORE_NAME,
      // Only each view's density preference survives a reload. Page numbers and
      // server totals must always start fresh: a stale page on a shared station is
      // wrong, and totals belong to whichever work order is open now.
      partialize: (state) => ({
        views: {
          current: {
            resourcesPerPage: state.views.current.resourcesPerPage,
            sort: state.views.current.sort
          },
          analyzed: {
            resourcesPerPage: state.views.analyzed.resourcesPerPage,
            sort: state.views.analyzed.sort
          }
        }
      }),
      // partialize stores a partial `views`, so the default shallow merge would
      // replace the whole map and leave currentPage/totals undefined.
      merge: (persisted, current) => {
        const persistedViews =
          (persisted as { views?: Partial<Record<InspectionListView, Partial<ViewPagination>>> })
            ?.views ?? {};

        return {
          ...current,
          views: {
            current: { ...current.views.current, ...(persistedViews.current ?? {}) },
            analyzed: {
              ...current.views.analyzed,
              ...(persistedViews.analyzed ?? {})
            }
          }
        };
      },
      // This callback runs SYNCHRONOUSLY inside create(), while the store binding
      // below is still in its temporal dead zone — so it must go through the `state`
      // argument, never through useInspectionPaginationStore.getState(). Reaching
      // for the binding here throws a ReferenceError during module evaluation, which
      // takes down every importer of this module and leaves the flag unset, gating
      // both fetching hooks forever.
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Failed to rehydrate inspection pagination.', error);
        }

        if (state) {
          state.setIsStoreHydrated(true);
          return;
        }

        // Storage threw, so there is no rehydrated state to set the flag through.
        // Deferring to a microtask puts the call after module evaluation, when the
        // binding exists. Without this the flag never flips and nothing ever fetches.
        queueMicrotask(() =>
          useInspectionPaginationStore.getState().setIsStoreHydrated(true)
        );
      }
    }
  )
);

/** One view's pagination — the shape both the hooks and the pagination bar read. */
export const useInspectionPagination = (view: InspectionListView) =>
  useInspectionPaginationStore((state) => state.views[view]);
